import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import { Box, Button, Flex, FormControl, FormLabel, Grid, GridItem, Heading, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, NumberDecrementStepper, NumberIncrementStepper, NumberInput, NumberInputField, NumberInputStepper, Select, Text, Textarea, VStack } from '@chakra-ui/react'
import { useImmer } from 'use-immer'
import { useAppContext } from 'hooks/useAppContext'
import { ModalProjectChangeOrderFormType } from './ModalProjectChangeOrderForm'
import { approveProjectChangeOrder } from 'api'
import { QUOTE_ADDITIONAL_ITEM_CATEGORIES } from 'lib'

const initialForm = {
  signedDoc: null,
  notes: ''
}

export const ModalProjectChangeOrderApprove = ({ isOpen, onClose, onEdit, type = ModalProjectChangeOrderFormType.Approve, changeOrder }) => {
  const { createToast } = useAppContext()
  const [form, setForm] = useImmer(initialForm)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    setForm(initialForm)
  }, [type, changeOrder])

  const handleSave = async (e) => {
    e.preventDefault()
    setIsSaving(true)
    try {
      await approveProjectChangeOrder(changeOrder?.id, { ...form })
      createToast('success', 'Success', 'Change order approved.')
      setIsSaving(false)
      onClose()
      setForm(initialForm)
    } catch (err) {
      setIsSaving(false)
      createToast('error', 'Error', 'Failed to save change order.')
    }
  }

  const handleChange = (e) => {
    setForm(draft => {
      draft[e.target.name] = e.target.value
    })
  }

  const handleFileChange = (e) => {
    const file = e.target.files?.[0]
    if (file) {
      setForm(draft => {
        draft[e.target.name] = e.target.files[0]
      })
    }
  }

  const lineItems = useMemo(() => {
    return changeOrder?.lineItems ? JSON.parse(changeOrder.lineItems) : []
  }, [changeOrder?.lineItems])

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSave}>
          <ModalHeader>Change Order</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack alignItems="none" gap={4}>
              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <GridItem>
                  <FormControl>
                    <FormLabel>Item Name</FormLabel>
                    <Input name="name" value={changeOrder?.name || ''} isReadOnly={true} />
                  </FormControl>
                </GridItem>
              </Grid>
              <Heading size="sm">Line Item(s)</Heading>
              <VStack alignItems="none" gap={2}>
                {!lineItems.length && (
                  <Text fontSize="sm">No line items.</Text>
                )}
                {lineItems.map(item => {
                  return (
                    <Box key={item.id}>
                      <FormControl>
                        <FormLabel>Item Description</FormLabel>
                        <Textarea name="description" value={item.item} rows="3" isReadOnly={true} />
                      </FormControl>
                      <Flex justifyContent="space-between" gap={4} alignItems="flex-end" mt={2}>
                        <FormControl>
                          <FormLabel>Category</FormLabel>
                          <Select name="category" value={item.category} placeholder="Select" isDisabled={true}>
                            {Object.keys(QUOTE_ADDITIONAL_ITEM_CATEGORIES).map(v => {
                              return (
                                <option key={QUOTE_ADDITIONAL_ITEM_CATEGORIES[v]} value={QUOTE_ADDITIONAL_ITEM_CATEGORIES[v]}>{QUOTE_ADDITIONAL_ITEM_CATEGORIES[v]}</option>
                              )
                            })}
                          </Select>
                        </FormControl>
                        <FormControl>
                          <FormLabel>Price</FormLabel>
                          <NumberInput
                            value={item.price}
                            min={0}
                            step={0.01}
                            isReadOnly={true}
                          >
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                        </FormControl>
                      </Flex>
                    </Box>
                  )
                })}
              </VStack>
              <Flex justifyContent="center" w="fit-content">
                <FormControl isRequired>
                  <FormLabel>Signed Doc</FormLabel>
                  <Input type="file" name="signedDoc" onChange={handleFileChange} />
                </FormControl>
              </Flex>
              <FormControl isRequired>
                <FormLabel>Notes</FormLabel>
                <Textarea name="notes" value={form.notes} onChange={handleChange} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Flex justifyContent="flex-end" gap={4}>
              <Button type="submit" colorScheme="blue" isLoading={isSaving}>
                Approve
              </Button>
              <Button onClick={() => { onEdit(changeOrder) }}>Edit</Button>
            </Flex>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}

ModalProjectChangeOrderApprove.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onEdit: PropTypes.func,
  type: PropTypes.oneOf(Object.values(ModalProjectChangeOrderFormType)),
  changeOrder: PropTypes.object,
  reload: PropTypes.func
}
